/**
 * LetterheadDesigner — Phase 21β-4.2
 *
 * Editor für das aktive Geschäftspapier (admin-only). Live-Vorschau rechts
 * zeigt jede Änderung sofort als gerenderte PDF (in einem <iframe>).
 *
 * Bearbeitbar:
 *  - Akzent-/Text-Farben (Color-Picker)
 *  - Slogan (z. B. "HAUSVERWALTUNG ANDERS DENKEN")
 *  - Datums-Label, Betreff-Optionen, Seitenzahl-Format
 *  - Schriftgrößen
 *  - Seitenränder (mm)
 *  - Logo-Position & -Breite (mm)
 *  - Multi-Spalten-Footer (Titel + Zeilen pro Spalte, beliebig viele Spalten)
 *
 * Speichern ruft PUT /api/settings/company; danach wird die Live-Vorschau neu
 * geladen. Versionierung läuft separat über das LetterheadVersionsPanel.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, API, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FloppyDisk, ArrowsClockwise, Trash, Plus, ArrowUp, ArrowDown,
  CaretRight, Lock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ADMIN_ROLES = ["super_admin", "platform_admin", "admin"];

const DEFAULTS = {
  letterhead_accent_color: "#1FB5A0",
  letterhead_dark_color: "#0F172A",
  letterhead_grey_color: "#64748B",
  letterhead_slogan: "",
  letterhead_date_label: "Datum:",
  letterhead_subject_uppercase: true,
  letterhead_subject_font_size: 11,
  letterhead_body_font_size: 10.5,
  letterhead_show_page_number: true,
  letterhead_page_number_format: "Seite {n}",
  letterhead_margin_top: 55,
  letterhead_margin_bottom: 30,
  letterhead_margin_left: 20,
  letterhead_margin_right: 15,
  letterhead_logo_x: 15,
  letterhead_logo_y: 8,
  letterhead_logo_width: 70,
  letterhead_footer_columns: [],
  // 2026-07 — Papier-Variante: "business" (Geschäftspapier) | "contract" (Vertragspapier)
  letterhead_kind: "business",
  // Ab Seite 2: Logo klein oben rechts — Breite (mm)
  letterhead_page2_logo_width: 25,
  // Phase VM-7e — Neu
  letterhead_hide_accents: false,
  letterhead_right_column: null,  // null = alter Default (Backend rendert Standardreihenfolge)
  // Phase VM-7f — DIN 5008 Fenstertyp + Body-Textausrichtung
  letterhead_din5008_window: "none",   // 'none' | 'A' | 'B'
  letterhead_body_text_align: "left",  // 'left' | 'justify'
  // Phase VM-7g — Footer & Right-Column Feinjustierung
  letterhead_footer_show_dividers: true,
  letterhead_footer_margin_left: 20,
  letterhead_footer_margin_right: 15,
  letterhead_right_column_x: 125,
  letterhead_right_column_width: 75,
};

// Verfügbare Zeilentypen für die rechte Spalte
const RIGHT_ROW_TYPES = [
  { value: "brand_name", label: "Firmenname (fett)" },
  { value: "sender_street", label: "Absender-Straße" },
  { value: "sender_zip_city", label: "Absender-PLZ + Ort" },
  { value: "phone", label: "Telefon" },
  { value: "email", label: "E-Mail" },
  { value: "web", label: "Internet / Website" },
  { value: "fax", label: "Fax" },
  { value: "mobile", label: "Mobil" },
  { value: "their_reference", label: "Ihr Zeichen" },
  { value: "my_reference", label: "Mein Zeichen" },
  { value: "author", label: "Bearbeiter" },
  { value: "date", label: "Datum" },
  { value: "business_account_block", label: "Geschäftskonto (Block)" },
  { value: "contract_number_block", label: "Mietvertragsnummer (Block)" },
  { value: "tenant_account_block", label: "Ihr Mietkonto (Block)" },
  { value: "custom", label: "Eigene Zeile (Label + Wert)" },
  { value: "spacer", label: "Abstand (spacer)" },
];

const RIGHT_COLUMN_DEFAULT = [
  { type: "brand_name", visible: true },
  { type: "sender_street", visible: true },
  { type: "sender_zip_city", visible: true },
  { type: "spacer", size: 1.5 },
  { type: "phone", label: "Telefon:", visible: true },
  { type: "email", label: "E-Mail:", visible: true },
  { type: "web", label: "Internet:", visible: true },
  { type: "spacer", size: 2 },
  { type: "their_reference", label: "Ihr Zeichen:", visible: true },
  { type: "my_reference", label: "Mein Zeichen:", visible: true, bold_value: true },
  { type: "author", label: "Bearbeiter:", visible: true },
  { type: "date", label: "Datum:", visible: true, bold_value: true },
  { type: "spacer", size: 2 },
  { type: "business_account_block", label: "Geschäftskonto:", visible: true },
  { type: "contract_number_block", label: "Ihre Mietvertragsnummer:", visible: true },
  { type: "tenant_account_block", label: "Ihr Mietkonto:", visible: true },
];

const FOOTER_PRESET_5_COL = [
  { title: "", lines: ["{{sender.company_name}}", "Inhaber: {{sender.owner_name}}", "{{sender.professional_title}}"] },
  { title: "", lines: ["{{sender.street}}", "{{sender.zip}} {{sender.city}}", "{{sender.country}}"] },
  { title: "Bankverbindung:", lines: ["{{account.business_bank}}", "BIC: {{account.business_bic}}", "IBAN: {{account.business_iban}}"] },
  { title: "Kontakt:", lines: ["Tel.: {{sender.phone}}", "E-Mail: {{sender.email}}", "Web: {{sender.web}}"] },
  { title: "Steuern:", lines: ["USt-IdNr.: {{sender.vat_id}}", "Steuer-Nr.: {{sender.tax_id}}", "AG: {{sender.register_court}} · {{sender.register_number}}"] },
];

export default function LetterheadDesigner() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(0); // bumps iframe src to force reload
  const [previewUrl, setPreviewUrl] = useState("");

  const loadPreview = useCallback(async () => {
    try {
      const r = await fetch(`${API}/letterheads/preview-current`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      setPreviewKey((k) => k + 1);
    } catch (e) {
      toast.error("Vorschau konnte nicht geladen werden");
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/company");
        setForm({ ...DEFAULTS, ...data });
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setCol = (i, k, v) => setForm((s) => {
    const cols = [...(s.letterhead_footer_columns || [])];
    cols[i] = { ...cols[i], [k]: v };
    return { ...s, letterhead_footer_columns: cols };
  });
  const addCol = () => setForm((s) => ({
    ...s,
    letterhead_footer_columns: [...(s.letterhead_footer_columns || []), { title: "Neue Spalte", lines: [""] }],
  }));
  const moveCol = (i, dir) => setForm((s) => {
    const cols = [...(s.letterhead_footer_columns || [])];
    const j = i + dir;
    if (j < 0 || j >= cols.length) return s;
    [cols[i], cols[j]] = [cols[j], cols[i]];
    return { ...s, letterhead_footer_columns: cols };
  });
  const removeCol = (i) => setForm((s) => ({
    ...s, letterhead_footer_columns: (s.letterhead_footer_columns || []).filter((_, idx) => idx !== i),
  }));
  const applyFooterPreset = () => setForm((s) => ({ ...s, letterhead_footer_columns: FOOTER_PRESET_5_COL }));

  // -------- Rechte Spalte (VM-7e) --------
  const rightRows = () => (form.letterhead_right_column && form.letterhead_right_column.length > 0) ? form.letterhead_right_column : RIGHT_COLUMN_DEFAULT;
  const setRightRows = (rows) => setForm((s) => ({ ...s, letterhead_right_column: rows }));
  const setRR = (i, k, v) => {
    const arr = [...rightRows()];
    arr[i] = { ...arr[i], [k]: v };
    setRightRows(arr);
  };
  const addRR = () => setRightRows([...rightRows(), { type: "custom", label: "", value: "", visible: true }]);
  const moveRR = (i, dir) => {
    const arr = [...rightRows()];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setRightRows(arr);
  };
  const removeRR = (i) => setRightRows(rightRows().filter((_, idx) => idx !== i));
  const resetRR = () => setRightRows([]); // Empty array wird Backend-seitig wie null behandelt (default rendering)

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/company", form);
      toast.success("Geschäftspapier gespeichert — wirkt sofort für neue Briefe");
      await loadPreview();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-slate-500 py-6 text-center">Lade…</div>;

  if (!isAdmin) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800 inline-flex items-center gap-2">
        <Lock size={16} /> Der Geschäftspapier-Editor ist nur für Administratoren freigeschaltet.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4" data-testid="letterhead-designer">
      {/* LEFT — Editor */}
      <div className="xl:col-span-7 space-y-3 max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
        {/* 2026-07 — Papier-Variante (Geschäft vs. Vertrag) */}
        <Section title="Papier-Variante" subtitle="Geschäftspapier für Schriftverkehr — Vertragspapier für Mietverträge, Vollmachten u. ä.">
          <Row>
            <Field label="Art des Briefpapiers">
              <Select value={form.letterhead_kind || "business"} onValueChange={(v) => set("letterhead_kind", v)}>
                <SelectTrigger data-testid="lh-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="business">Geschäftspapier (Logo links, klassischer Footer)</SelectItem>
                  <SelectItem value="contract">Vertragspapier (Logo mittig, © Firma Jahr + Seitenzahl)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Ab Seite 2 — Logo klein oben rechts, Breite (mm)">
              <Input
                type="number" min={15} max={60} step={1}
                value={form.letterhead_page2_logo_width ?? 25}
                onChange={(e) => set("letterhead_page2_logo_width", Number(e.target.value) || 25)}
                data-testid="lh-page2-logo-width"
              />
            </Field>
          </Row>
          <div className="text-[11px] text-slate-500 mt-1">
            Ab Seite 2 wird das Logo automatisch verkleinert oben rechts gesetzt — sowohl beim Geschäfts- als auch beim Vertragspapier.
          </div>
        </Section>

        <Section title="Design & Akzente" subtitle="Alle farbigen Streifen und den Slogan-Bereich komplett ausblenden.">
          <Row>
            <Field label="Farb-Akzente anzeigen?">
              <Select value={form.letterhead_hide_accents ? "no" : "yes"}
                      onValueChange={(v) => set("letterhead_hide_accents", v === "no")}>
                <SelectTrigger data-testid="lh-hide-accents"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Ja — mit Akzentstreifen und Slogan</SelectItem>
                  <SelectItem value="no">Nein — schlicht schwarz/grau (empfohlen)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="DIN 5008 Anschriftfenster (für Fensterumschläge)">
              <Select value={form.letterhead_din5008_window || "none"}
                      onValueChange={(v) => set("letterhead_din5008_window", v)}>
                <SelectTrigger data-testid="lh-din5008"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein DIN-Fenster (Standard-Position)</SelectItem>
                  <SelectItem value="A">Typ A — Kurzform (Fenster 27–72 mm)</SelectItem>
                  <SelectItem value="B">Typ B — Langform (Fenster 45–90 mm)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Text-Ausrichtung Fließtext">
              <Select value={form.letterhead_body_text_align || "left"}
                      onValueChange={(v) => set("letterhead_body_text_align", v)}>
                <SelectTrigger data-testid="lh-body-align"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Linksbündig (Standard)</SelectItem>
                  <SelectItem value="justify">Blocksatz</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </Row>
        </Section>

        <Section title="Farben">
          <Row>
            <Field label="Akzentfarbe (Streifen)">
              <ColorInput value={form.letterhead_accent_color} onChange={(v) => set("letterhead_accent_color", v)} testid="lh-color-accent" />
            </Field>
            <Field label="Textfarbe (dunkel)">
              <ColorInput value={form.letterhead_dark_color} onChange={(v) => set("letterhead_dark_color", v)} testid="lh-color-dark" />
            </Field>
            <Field label="Textfarbe (grau / Labels)">
              <ColorInput value={form.letterhead_grey_color} onChange={(v) => set("letterhead_grey_color", v)} testid="lh-color-grey" />
            </Field>
          </Row>
        </Section>

        <Section title="Kopfbereich">
          <Row>
            <Field label="Slogan (oben rechts im Akzent-Streifen)" className="col-span-2">
              <Input value={form.letterhead_slogan || ""} onChange={(e) => set("letterhead_slogan", e.target.value)}
                     placeholder="z. B. HAUSVERWALTUNG ANDERS DENKEN" data-testid="lh-slogan" />
            </Field>
            <Field label="Datums-Label">
              <Input value={form.letterhead_date_label || ""} onChange={(e) => set("letterhead_date_label", e.target.value)}
                     placeholder="Datum:" data-testid="lh-date-label" />
            </Field>
          </Row>
          <Row>
            <Field label="Logo X (mm)"><NumberInput value={form.letterhead_logo_x} onChange={(v) => set("letterhead_logo_x", v)} testid="lh-logo-x" /></Field>
            <Field label="Logo Y (mm)"><NumberInput value={form.letterhead_logo_y} onChange={(v) => set("letterhead_logo_y", v)} testid="lh-logo-y" /></Field>
            <Field label="Logo Breite (mm)"><NumberInput value={form.letterhead_logo_width} onChange={(v) => set("letterhead_logo_width", v)} testid="lh-logo-w" /></Field>
          </Row>
        </Section>

        <Section title="Typografie & Betreff">
          <Row>
            <Field label="Betreff in GROSSBUCHSTABEN">
              <Select value={form.letterhead_subject_uppercase ? "yes" : "no"}
                      onValueChange={(v) => set("letterhead_subject_uppercase", v === "yes")}>
                <SelectTrigger data-testid="lh-subject-upper"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Ja (klassisch)</SelectItem>
                  <SelectItem value="no">Nein (gemischte Schreibweise)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Betreff Schriftgröße (pt)">
              <NumberInput value={form.letterhead_subject_font_size} onChange={(v) => set("letterhead_subject_font_size", v)} testid="lh-subject-size" />
            </Field>
            <Field label="Brief-Schriftgröße (pt)">
              <NumberInput value={form.letterhead_body_font_size} onChange={(v) => set("letterhead_body_font_size", v)} testid="lh-body-size" />
            </Field>
          </Row>
        </Section>

        <Section title="Seitenränder (mm)">
          <Row>
            <Field label="Oben"><NumberInput value={form.letterhead_margin_top} onChange={(v) => set("letterhead_margin_top", v)} testid="lh-mt" /></Field>
            <Field label="Unten"><NumberInput value={form.letterhead_margin_bottom} onChange={(v) => set("letterhead_margin_bottom", v)} testid="lh-mb" /></Field>
            <Field label="Links"><NumberInput value={form.letterhead_margin_left} onChange={(v) => set("letterhead_margin_left", v)} testid="lh-ml" /></Field>
            <Field label="Rechts"><NumberInput value={form.letterhead_margin_right} onChange={(v) => set("letterhead_margin_right", v)} testid="lh-mr" /></Field>
          </Row>
        </Section>

        <Section title="Seitenzahl">
          <Row>
            <Field label="Anzeigen?">
              <Select value={form.letterhead_show_page_number ? "yes" : "no"}
                      onValueChange={(v) => set("letterhead_show_page_number", v === "yes")}>
                <SelectTrigger data-testid="lh-page-num"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Ja</SelectItem>
                  <SelectItem value="no">Nein</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Format (verwende {n} für die Seitenzahl)" className="col-span-2">
              <Input value={form.letterhead_page_number_format || ""} onChange={(e) => set("letterhead_page_number_format", e.target.value)}
                     placeholder="Seite {n}" data-testid="lh-page-format" />
            </Field>
          </Row>
        </Section>

        <Section title="Rechte Spalte" subtitle="Zeilen ein/aus, Reihenfolge, eigene Zeilen (Fax, Mobil …). Reihenfolge = Anzeigereihenfolge oben rechts.">
          {rightRows().map((r, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-2 mb-1.5 bg-slate-50/50 flex items-start gap-2" data-testid={`lh-rr-${i}`}>
              <div className="flex flex-col gap-1 pt-1 shrink-0">
                <Button size="icon" variant="ghost" onClick={() => moveRR(i, -1)} disabled={i === 0} className="h-6 w-6"><ArrowUp size={12} /></Button>
                <Button size="icon" variant="ghost" onClick={() => moveRR(i, 1)} disabled={i === rightRows().length - 1} className="h-6 w-6"><ArrowDown size={12} /></Button>
              </div>
              <div className="flex-1 min-w-0 grid grid-cols-12 gap-2 items-center">
                <div className="col-span-4">
                  <Label className="text-[10px] uppercase text-slate-500">Typ</Label>
                  <Select value={r.type} onValueChange={(v) => setRR(i, "type", v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`lh-rr-${i}-type`}><SelectValue /></SelectTrigger>
                    <SelectContent>{RIGHT_ROW_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {r.type === "spacer" && (
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase text-slate-500">Größe (mm)</Label>
                    <Input type="number" step="0.5" className="h-8 text-xs" value={r.size ?? 1.5}
                      onChange={(e) => setRR(i, "size", Number(e.target.value))} data-testid={`lh-rr-${i}-size`} />
                  </div>
                )}
                {r.type !== "spacer" && (
                  <>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase text-slate-500">Label</Label>
                      <Input className="h-8 text-xs" value={r.label ?? ""}
                        onChange={(e) => setRR(i, "label", e.target.value)}
                        placeholder="z.B. Telefon:" data-testid={`lh-rr-${i}-label`} />
                    </div>
                    {r.type === "custom" && (
                      <div className="col-span-3">
                        <Label className="text-[10px] uppercase text-slate-500">Wert (Text oder Makro)</Label>
                        <Input className="h-8 text-xs" value={r.value ?? ""}
                          onChange={(e) => setRR(i, "value", e.target.value)}
                          placeholder="z.B. {{sender.phone}}" data-testid={`lh-rr-${i}-value`} />
                      </div>
                    )}
                    <div className={`${r.type === "custom" ? "col-span-2" : "col-span-5"} flex items-center justify-end gap-2`}>
                      <label className="text-[11px] text-slate-700 flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={r.visible !== false}
                          onChange={(e) => setRR(i, "visible", e.target.checked)}
                          data-testid={`lh-rr-${i}-visible`} />
                        Sichtbar
                      </label>
                    </div>
                  </>
                )}
              </div>
              <Button size="icon" variant="ghost" onClick={() => removeRR(i)}
                      className="h-7 w-7 text-red-700 hover:bg-red-50 shrink-0" data-testid={`lh-rr-${i}-remove`}>
                <Trash size={13} />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Button variant="outline" onClick={addRR} className="gap-2" data-testid="lh-rr-add">
              <Plus size={14} /> Zeile hinzufügen
            </Button>
            <Button variant="outline" onClick={resetRR} className="gap-2" data-testid="lh-rr-reset">
              <ArrowsClockwise size={14} /> Auf Standard zurücksetzen
            </Button>
          </div>
        </Section>

        <Section title="Footer-Spalten" subtitle="Beliebig viele Spalten — jede mit Titel und mehreren Zeilen.">
          {(form.letterhead_footer_columns || []).map((c, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-3 mb-2 bg-slate-50/50">
              <div className="flex items-center gap-1 mb-2">
                <Input value={c.title || ""} onChange={(e) => setCol(i, "title", e.target.value)}
                       placeholder="Spaltentitel (z. B. Geschäftskonto:)"
                       className="font-semibold text-sm" data-testid={`lh-col-${i}-title`} />
                <Button size="icon" variant="ghost" onClick={() => moveCol(i, -1)} disabled={i === 0} className="h-8 w-8"><ArrowUp size={14} /></Button>
                <Button size="icon" variant="ghost" onClick={() => moveCol(i, 1)} disabled={i === (form.letterhead_footer_columns.length - 1)} className="h-8 w-8"><ArrowDown size={14} /></Button>
                <Button size="icon" variant="ghost" onClick={() => removeCol(i)} className="h-8 w-8 text-red-700 hover:bg-red-50"><Trash size={14} /></Button>
              </div>
              <textarea
                value={(c.lines || []).join("\n")}
                onChange={(e) => setCol(i, "lines", e.target.value.split("\n"))}
                rows={4}
                className="w-full text-xs font-mono leading-snug border border-slate-200 rounded-sm px-2 py-1.5 bg-white focus:outline-none focus:border-slate-400"
                placeholder={"Zeile 1\nZeile 2\nZeile 3"}
                data-testid={`lh-col-${i}-lines`}
              />
            </div>
          ))}
          <Button variant="outline" onClick={addCol} className="gap-2" data-testid="lh-col-add">
            <Plus size={14} /> Footer-Spalte hinzufügen
          </Button>
          <Button variant="outline" onClick={applyFooterPreset} className="gap-2 ml-2" data-testid="lh-footer-preset">
            5-Spalten-Preset laden (Firma · Adresse · Bank · Kontakt · Steuern)
          </Button>
        </Section>

        <Section title="Footer-Details" subtitle="Trennlinien, Ränder und Spaltenbreite steuern (verhindert Überlauf langer Werte wie IBAN).">
          <Row>
            <Field label="Trennstriche zwischen Spalten?">
              <Select value={form.letterhead_footer_show_dividers ? "yes" : "no"}
                      onValueChange={(v) => set("letterhead_footer_show_dividers", v === "yes")}>
                <SelectTrigger data-testid="lh-footer-dividers"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Ja — mit vertikalen Trennlinien</SelectItem>
                  <SelectItem value="no">Nein — nur die obere Trennlinie</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Footer-Rand links (mm)">
              <NumberInput value={form.letterhead_footer_margin_left} onChange={(v) => set("letterhead_footer_margin_left", v)} testid="lh-footer-ml" />
            </Field>
            <Field label="Footer-Rand rechts (mm)">
              <NumberInput value={form.letterhead_footer_margin_right} onChange={(v) => set("letterhead_footer_margin_right", v)} testid="lh-footer-mr" />
            </Field>
          </Row>
          <div className="text-[10px] text-slate-500 -mt-1">
            Tipp: Wenn die IBAN überläuft, reduziere die Ränder auf z.B. 10/10 mm — die Spalten werden dadurch automatisch breiter.
          </div>
        </Section>

        <Section title="Rechte Spalte — Position & Breite" subtitle="Größer machen, wenn Werte wie 'IBAN' oder 'Ihr Mietkonto' abgeschnitten werden.">
          <Row>
            <Field label="X-Position (mm ab links)">
              <NumberInput value={form.letterhead_right_column_x} onChange={(v) => set("letterhead_right_column_x", v)} testid="lh-rc-x" />
            </Field>
            <Field label="Breite (mm)">
              <NumberInput value={form.letterhead_right_column_width} onChange={(v) => set("letterhead_right_column_width", v)} testid="lh-rc-w" />
            </Field>
          </Row>
          <div className="text-[10px] text-slate-500 -mt-1">
            Standard: X=125, Breite=75. Bei Werten wie langen IBANs → Breite auf 80–85 erhöhen. Achte darauf, dass X + Breite ≤ 205 bleibt.
          </div>
        </Section>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 pt-3 -mx-1 px-1 flex gap-2">
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2 flex-1" data-testid="lh-save-btn">
            <FloppyDisk size={14} /> {saving ? "Speichere…" : "Speichern (wirkt sofort)"}
          </Button>
          <Button variant="outline" onClick={loadPreview} className="gap-2" data-testid="lh-reload-preview">
            <ArrowsClockwise size={14} /> Vorschau aktualisieren
          </Button>
        </div>
      </div>

      {/* RIGHT — Live-Vorschau */}
      <div className="xl:col-span-5">
        <div className="bg-slate-100 border border-slate-200 rounded-md p-2 sticky top-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5 inline-flex items-center gap-1 px-1">
            <CaretRight size={10} /> Live-Vorschau (auf Basis der aktiven Settings)
          </div>
          {previewUrl ? (
            <iframe
              key={previewKey} src={previewUrl}
              className="w-full bg-white rounded shadow-sm"
              style={{ height: "calc(100vh - 250px)", minHeight: "560px" }}
              title="Geschäftspapier-Vorschau"
              data-testid="lh-preview-iframe"
            />
          ) : (
            <div className="bg-white rounded shadow-sm flex items-center justify-center text-sm text-slate-400" style={{ height: "560px" }}>
              Vorschau wird geladen…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- helpers -----
function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
        {title} {subtitle && <span className="ml-2 normal-case tracking-normal text-slate-400">— {subtitle}</span>}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">{children}</div>;
}
function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[11px] text-slate-600">{label}</Label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
function ColorInput({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1.5">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())}
             className="h-8 w-10 border border-slate-200 rounded cursor-pointer" data-testid={testid} />
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
             className="h-8 text-sm font-mono" placeholder="#RRGGBB" />
    </div>
  );
}
function NumberInput({ value, onChange, testid }) {
  return (
    <Input type="number" step="0.5" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
           className="h-8 text-sm" data-testid={testid} />
  );
}
